import type { TableCellKey } from '../../stores/tableCellStore';
import { BaseNodeCommand, type NodeChangeRecord } from './base/BaseNodeCommand';

export type TableCellLayoutAlgorithmId = 'fixed' | 'grid';

export type TableCellLayoutConfig = {
  algorithmId: TableCellLayoutAlgorithmId;
  params?: Record<string, unknown>;
};

export type UpdateTableCellLayoutsParams = {
  nodeIds: string[];
  cellKeysByNodeId: Record<string, TableCellKey[]>;
  layout: TableCellLayoutConfig;
};

export class UpdateTableCellLayoutsCommand extends BaseNodeCommand {
  constructor(private params: UpdateTableCellLayoutsParams) {
    super('Update Table Cell Layout', `Set layout: ${params.layout.algorithmId}`);
  }

  protected doExecute(): NodeChangeRecord[] {
    const records: NodeChangeRecord[] = [];
    const mm: any = this.getMutationManager();

    const updates: Array<{ nodeId: string; changes: any }> = [];

    for (const nodeId of this.params.nodeIds) {
      const node = mm.getNode(nodeId) as any;
      if (!node) continue;
      if (node.type !== 'table') continue;

      const keys = this.params.cellKeysByNodeId[nodeId] ?? [];
      if (keys.length === 0) continue;

      const attrs = (node.attrs ?? {}) as Record<string, unknown>;
      const prev = ((attrs as any).cellLayouts ?? {}) as Record<string, TableCellLayoutConfig>;
      const next: Record<string, TableCellLayoutConfig> = { ...prev };

      for (const k of keys) {
        if (this.params.layout.algorithmId === 'fixed') {
          delete next[k];
        } else {
          next[k] = { algorithmId: this.params.layout.algorithmId, params: this.params.layout.params };
        }
      }

      const nextAttrs: Record<string, unknown> = { ...attrs };
      if (Object.keys(next).length === 0) {
        // Signal explicit deletion so mutation manager removes this key
        (nextAttrs as any).cellLayouts = undefined;
      } else {
        (nextAttrs as any).cellLayouts = next;
      }

      updates.push({ nodeId, changes: { attrs: nextAttrs } });
    }

    if (updates.length > 1 && typeof mm.applyBatchNodeChanges === 'function') {
      const batch = mm.applyBatchNodeChanges(updates);
      records.push(...(batch ?? []));
    } else if (updates.length === 1) {
      const rec = mm.applyNodeChange(updates[0].nodeId, updates[0].changes);
      if (rec) records.push(rec);
    }

    return records;
  }
}
